import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import QRCode from "react-fancy-qrcode";
import { TitleAndActionBox } from "../../../common";
import { ParagraphText, UBButton } from "../../../../Libraries";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { useWalletContext } from "../../../../context/WalletContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import {
  CopyIcon,
  GreenCheck,
  RaastApprovedLogo,
  RupinFavIcon,
} from "../../../../assets/icons/icon";
import LogoRupin from "../../../../assets/icons/Logo Rupin.svg";
import { sendBanKDetailToWhatsApp } from "../../../../utils";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { handleApi } from "../../../../apis";
import {
  GET_RAAST_JOB_STATUS,
  POST_RAAST_REGISTER_ALIAS,
  POST_RAAST_UNLINK_ALIAS,
} from "../../../../apis/configuration";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";
import styles from "./styles.module.css";

const RAAST_JOB_POLL_INTERVAL = 5000;

const WalletAccountDetail = () => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { walletData, fetchWalletData } = useWalletContext();
  const { showSnackbar } = useSnackbar();
  const [copyKey, setCopyKey] = useState("");
  const [raastLinkLoad, setRaastLinkLoad] = useState(false);
  const [unlinkModalOpen, setUnlinkModalOpen] = useState(false);
  const jobPollRef = useRef(null);

  const raastDetail = walletData?.account_info?.raast_info?.raast;
  const raastDetails = raastDetail?.raast_details;

  const accountDetails = useMemo(
    () => ({
      bankName: raastDetail?.bank_name || "RUPIN",
      accountTitle: raastDetails?.name || "",
      accountNumber: raastDetails?.account_number || "",
      iban: raastDetails?.iban || "",
      qr_share_key: raastDetails?.qr_share_key,
    }),
    [raastDetail?.bank_name, raastDetails],
  );

  const raastId = raastDetails?.raast_id || user?.phone_number || "";
  const raastLinkedStatus = raastDetail?.raast_id_status || "";
  const qrValue = raastDetails?.qr_code || "";
  const last4 = accountDetails.accountNumber.slice(-4);

  useEffect(() => {
    return () => clearInterval(jobPollRef.current);
  }, []);

  const stopRaastPolling = () => {
    if (jobPollRef.current) {
      clearInterval(jobPollRef.current);
      jobPollRef.current = null;
    }
  };

  const handleRaastJobStatus = (jobId, jobType) => {
    if (!jobId) {
      setRaastLinkLoad(false);
      return;
    }
    jobPollRef.current = setInterval(async () => {
      try {
        const resp = await handleApi(
          GET_RAAST_JOB_STATUS,
          { job_id: jobId },
          null,
          navigate,
        );
        if (resp?.status === "COMPLETED" || resp?.status === "FAILED") {
          stopRaastPolling();
          setRaastLinkLoad(false);
          setUnlinkModalOpen(false);
          if (resp?.status === "COMPLETED") {
            triggerLogEvent(
              analytics,
              jobType === "link" ? "w_raast_id_linked" : "w_raast_id_unlinked",
            );
            fetchWalletData({ navigate });
            showSnackbar({
              message:
                jobType === "link"
                  ? I18n.raast_id_linked_successfully
                  : I18n.raast_id_unlinked_successfully,
              severity: "success",
            });
          } else {
            showSnackbar({
              message: I18n.something_went_wrong,
              severity: "error",
            });
          }
        }
      } catch (error) {
        stopRaastPolling();
        setRaastLinkLoad(false);
        showSnackbar({
          message: error?.response?.data?.message || I18n.something_went_wrong,
          severity: "error",
        });
      }
    }, RAAST_JOB_POLL_INTERVAL);
  };

  const handleLinkRaast = async () => {
    if (raastLinkLoad) return;
    triggerLogEvent(analytics, "w_raast_id_link");
    setRaastLinkLoad(true);
    try {
      const resp = await handleApi(
        POST_RAAST_REGISTER_ALIAS,
        {},
        null,
        navigate,
      );
      handleRaastJobStatus(resp?.job_id, "link");
    } catch (error) {
      setRaastLinkLoad(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.something_went_wrong,
        severity: "error",
      });
    }
  };

  const handleUnlinkRaast = async () => {
    if (raastLinkLoad) return;
    triggerLogEvent(analytics, "w_raast_id_unlink");
    setRaastLinkLoad(true);
    try {
      const resp = await handleApi(
        POST_RAAST_UNLINK_ALIAS,
        {},
        null,
        navigate,
      );
      handleRaastJobStatus(resp?.job_id, "unlink");
    } catch (error) {
      setRaastLinkLoad(false);
      setUnlinkModalOpen(false);
      showSnackbar({
        message: error?.response?.data?.message || I18n.something_went_wrong,
        severity: "error",
      });
    }
  };

  const toggleUnlinkModal = () => {
    if (raastLinkLoad) return;
    setUnlinkModalOpen((prev) => !prev);
  };

  const handleCopy = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyKey(key);
      triggerLogEvent(analytics, "w_account_detail_copy", { type: key });
      setTimeout(() => setCopyKey(""), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  const handleShare = () => {
    triggerLogEvent(analytics, "w_account_detail_share");
    sendBanKDetailToWhatsApp({
      bankName: accountDetails.bankName,
      accountTitle: accountDetails.accountTitle,
      accountNumber: accountDetails.accountNumber,
      iban: accountDetails.iban,
      qr_share_key: accountDetails.qr_share_key,
    });
  };

  return (
    <Fragment>
      <Grid
        container
        className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${styles.wrapper}`}
      >
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.account_details,
              isTitleCase: false,
              isBack: true,
              onBack: () => navigate(-1),
              id: "account_details",
            }}
          />
 {qrValue && (
              <Box className={styles.qrSection}>
                <Box className={`${styles.qrCodeBox} qrContrastSurface`}>
                  <QRCode
                    value={qrValue}
                    size={202}
                    dotRadius="12%"
                    dotScale={1}
                    positionRadius={5}
                    logo={LogoRupin}
                  />
                </Box>
                <ParagraphText className={styles.qrCaption}>
                  {accountDetails.accountTitle}
                  {last4 ? ` – ${last4}` : ""}
                </ParagraphText>
              </Box>
            )}


          <Box className={styles.content}>
            <Box className={styles.accountCard}>
              <Box className={styles.accountField}>
                <ParagraphText className={styles.fieldLabel}>
                  {I18n.account_title}
                </ParagraphText>
                <ParagraphText className={styles.fieldValue}>
                  {accountDetails.accountTitle}
                </ParagraphText>
              </Box>

              <Box className={styles.accountField}>
                <ParagraphText className={styles.fieldLabel}>
                  {I18n.account_no}
                </ParagraphText>
                <Box className={styles.fieldValueRow}>
                  <ParagraphText className={`${styles.fieldValue} ${styles.tabularNums}`}>
                    {accountDetails.accountNumber}
                  </ParagraphText>
                  <button
                    id="account_detail_copy_account_number_btn"
                    type="button"
                    className={styles.copyIconBtn}
                    onClick={() =>
                      handleCopy(accountDetails.accountNumber, "accountNumber")
                    }
                    aria-label={I18n.copy}
                  >
                    {copyKey === "accountNumber" ? (
                      <span className={styles.copiedFeedback}>
                        <GreenCheck width={13} height={13} />
                        <span>{I18n.copied}</span>
                      </span>
                    ) : (
                      <CopyIcon width={16} height={16} />
                    )}
                  </button>
                </Box>
              </Box>

              <Box className={styles.accountField}>
                <ParagraphText className={styles.fieldLabel}>
                  {I18n.IBAN}
                </ParagraphText>
                <Box className={styles.fieldValueRow}>
                  <ParagraphText className={`${styles.fieldValue} ${styles.tabularNums}`}>
                    {accountDetails.iban}
                  </ParagraphText>
                  <button
                    id="account_detail_copy_iban_btn"
                    type="button"
                    className={styles.copyIconBtn}
                    onClick={() => handleCopy(accountDetails.iban, "iban")}
                    aria-label={I18n.copy}
                  >
                    {copyKey === "iban" ? (
                      <span className={styles.copiedFeedback}>
                        <GreenCheck width={13} height={13} />
                        <span>{I18n.copied}</span>
                      </span>
                    ) : (
                      <CopyIcon width={16} height={16} />
                    )}
                  </button>
                </Box>
              </Box>

              <Box className={styles.accountField}>
                <ParagraphText className={styles.fieldLabel}>
                  {I18n.bank}
                </ParagraphText>
                <Box className={styles.fieldValueRow}>
                  <ParagraphText className={styles.fieldValue}>
                    {accountDetails.bankName}
                  </ParagraphText>
                  <span className={styles.bankLogoBadge}>
                    <RupinFavIcon />
                  </span>
                </Box>
              </Box>

              {raastId && (
                <Box className={styles.accountField}>
                  <ParagraphText className={styles.fieldLabel}>
                    {I18n.raast_id}
                  </ParagraphText>
                  <Box className={styles.fieldValueRow}>
                    <ParagraphText className={`${styles.fieldValue} ${styles.tabularNums}`}>
                      {raastId}
                    </ParagraphText>
                    {raastLinkedStatus === "linked" ? (
                      <UBButton
                        id="account_detail_unlink_raast_btn"
                        variant="secondary"
                        title={I18n.unlink}
                        onClick={toggleUnlinkModal}
                        className={`ubSecondaryButton textUppercase ${styles.unlinkBtn}`}
                      />
                    ) : (
                      <UBButton
                        id="account_detail_link_raast_btn"
                        variant="primary"
                        title={I18n.link}
                        onClick={handleLinkRaast}
                        disabled={raastLinkLoad}
                        loading={raastLinkLoad}
                        spinnerColor="mainColor"
                        className={`ubPrimaryButton textUppercase ${styles.linkRaastBtn}`}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>

           
            <Box className={styles.sbpBadge}>
              <RaastApprovedLogo />
              <ParagraphText className={styles.sbpBadgeText}>
                {I18n.licensed_by_sbp_in_principle}
              </ParagraphText>
            </Box>
            <UBButton
              id="account_detail_share_btn"
              title={I18n.share}
              onClick={handleShare}
              className={`${styles.shareBtn} ubButtonStyle ubPrimaryButton textUppercase`}
            />
          </Box>
        </Grid>
      </Grid>

      <ConfirmationModal
        id="raast_id_unlink_modal"
        open={unlinkModalOpen}
        onClose={toggleUnlinkModal}
        onConfirm={handleUnlinkRaast}
        message={I18n.raast_id_unlink}
        title={I18n.raast_id_unlink_desc}
        confirmText={I18n.unlink}
        cancelText={I18n.cancel}
        loading={raastLinkLoad}
      />
    </Fragment>
  );
};

export default WalletAccountDetail;
